import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, type ProductFilter, type ProductQuery } from "@/api";

export const productKeys = {
	all: ["products"] as const,
	list: (filter: ProductFilter) => ["products", "list", filter] as const,
	detail: (id: string) => ["products", "detail", id] as const,
	rejectionReasons: ["products", "rejection-reasons"] as const,
	statusCounts: (query: ProductQuery = {}) => ["products", "status-counts", query] as const,
	filterOptions: ["products", "filter-options"] as const,
};

// Справочник типовых причин отклонения — грузим один раз, кэш почти вечный
// (набор меняется редко), чтобы модалка отклонения открывалась мгновенно.
export function useRejectionReasons() {
	return useQuery({
		queryKey: productKeys.rejectionReasons,
		queryFn: () => api.products.rejectionReasons(),
		staleTime: 60 * 60 * 1000,
	});
}

// Страница с сервера. Пока грузится следующая, показываем прежнюю — таблица не
// схлопывается в «Загрузка…» на каждом клике по странице или фильтру.
export function useProducts(filter: ProductFilter) {
	return useQuery({
		queryKey: productKeys.list(filter),
		queryFn: () => api.products.list(filter),
		placeholderData: keepPreviousData,
	});
}

/** Счётчики по статусам в текущем срезе (магазин, категория, поиск, даты). */
export function useProductStatusCounts(query: ProductQuery = {}) {
	return useQuery({
		queryKey: productKeys.statusCounts(query),
		queryFn: () => api.products.statusCounts(query),
		staleTime: 30 * 1000,
		placeholderData: keepPreviousData,
	});
}

/** Магазины, категории и тимлиды для выпадающих фильтров. */
export function useProductFilterOptions() {
	return useQuery({
		queryKey: productKeys.filterOptions,
		queryFn: () => api.products.filterOptions(),
		staleTime: 5 * 60 * 1000,
	});
}

export function useProduct(id: string) {
	return useQuery({
		queryKey: productKeys.detail(id),
		queryFn: () => api.products.get(id),
		enabled: !!id,
	});
}
